import { qiCommentMatchesPerson, type QiCommentPerson } from '../comment-identifiers';
import { QI_FIELDS } from '../manifest';
import type { QiValue } from '../types';

export interface QiCommentWarning {
  category: 'POLICY';
  code: 'comment_identifier_review' | 'comment_population_check_unavailable';
  fieldCode?: string;
  message: string;
}
/** Warning only. Never replaces a method explanation or includes identifying text
 * in the returned diagnostics. The provider remains responsible for de-identifying. */
export function qiCommentWarnings(
  values: Readonly<Record<string, QiValue>>,
  people: readonly QiCommentPerson[] = [],
): QiCommentWarning[] {
  const warnings: QiCommentWarning[] = [];
  for (const field of QI_FIELDS.filter((f) => f.kind === 'comment')) {
    const v = values[field.code];
    if (v?.state !== 'measured' || typeof v.value !== 'string' || !v.value.trim()) continue;
    const text = v.value.normalize('NFKC').toLocaleLowerCase('en-AU');
    const personMatch = qiCommentMatchesPerson(v.value, people);
    if (
      personMatch ||
      /\b(?:dob|date\s+of\s+birth)\b/i.test(text) ||
      /\d{6,}/.test(text) ||
      /\b(?:19|20)\d{2}-\d{2}-\d{2}\b/.test(text) ||
      /\b\d{1,2}[/-]\d{1,2}[/-](?:19|20)\d{2}\b/.test(text)
    )
      warnings.push({
        category: 'POLICY',
        code: 'comment_identifier_review',
        fieldCode: field.code,
        message:
          'This comment may contain a resident identifier. Review and de-identify it before lodgement. The explanation is exported unchanged.',
      });
  }
  return warnings;
}

/**
 * Manual Part A (September 2026): the weight-loss and ADL comments must explain why
 * weights or the previous assessment were not recorded. They are required only when
 * that exclusion count is above zero. Every other comment may be blank.
 */
export const QI_CONDITIONAL_COMMENT_TRIGGERS: Readonly<Record<string, string>> = {
  'UPWL-06': 'UPWL-04',
  'UPWL-13': 'UPWL-11',
  'ADL-07': 'ADL-04',
};
export function qiCommentRequired(
  code: string,
  values: Readonly<Record<string, QiValue>>,
): boolean {
  const trigger = QI_CONDITIONAL_COMMENT_TRIGGERS[code];
  if (!trigger) return false;
  const count = values[trigger];
  return count?.state === 'measured' && typeof count.value === 'number' && count.value > 0;
}
